/**
 * PUSH na tela — notificação no estilo do sistema, dentro do app (web).
 *
 *   const id = push.mostrar({ titulo, mensagem, tom: "info", acoes: [...] });
 *   push.atualizar(id, { carregando: true });   // muda o conteúdo sem piscar
 *   push.fechar(id);                            // sai com animação
 *
 * O <PushHost /> (montado no RootLayout) desenha a pilha: no celular no topo, na largura
 * da tela; no desktop no canto superior direito. Some sozinho depois de `duracaoMs` (barra
 * de tempo; para enquanto o mouse está em cima ou enquanto `carregando`), fecha no X ou
 * arrastando para o lado. `chave` evita empilhar o mesmo aviso: mostrar de novo atualiza.
 */

export type TomPush = "info" | "sucesso" | "alerta" | "erro";

export type AcaoPush = {
  rotulo: string;
  /** Botão cheio (a ação principal). */
  principal?: boolean;
  /** Não fecha o push ao tocar (a ação atualiza o conteúdo, ex.: "Sincronizar agora"). */
  manterAberto?: boolean;
  aoTocar?: () => void;
};

export type DadosPush = {
  titulo: string;
  mensagem?: string;
  /** Nome do MaterialCommunityIcons; sem ele, o ícone do tom. */
  icone?: string;
  tom?: TomPush;
  acoes?: AcaoPush[];
  /** Some sozinho depois disto (ms). null/0 = fica até fechar. Padrão: 8 s. */
  duracaoMs?: number | null;
  /** Mostra o carregando no lugar do ícone e segura o "some sozinho". */
  carregando?: boolean;
  /** Um aviso por chave: mostrar de novo com a mesma chave atualiza o que já está na tela. */
  chave?: string;
  aoFechar?: () => void;
};

export type Push = DadosPush & {
  id: string;
  criadoEm: number;
  /** Muda a cada `atualizar` — reinicia a barra de tempo. */
  versao: number;
  saindo?: boolean;
};

/** No máximo isto na tela; os mais antigos saem. */
const MAXIMO = 3;
const DURACAO_SAIDA_MS = 240;

let lista: Push[] = [];
const ouvintes = new Set<() => void>();
let seq = 0;

function emitir() {
  for (const fn of [...ouvintes]) fn();
}

export const push = {
  mostrar(d: DadosPush): string {
    if (d.chave) {
      const ja = lista.find((p) => p.chave === d.chave && !p.saindo);
      if (ja) {
        push.atualizar(ja.id, d);
        return ja.id;
      }
    }
    const id = `push-${++seq}`;
    const novo: Push = { duracaoMs: 8000, ...d, id, criadoEm: Date.now(), versao: 0 };
    const ativos = lista.filter((p) => !p.saindo);
    lista = [novo, ...lista];
    // Passou do máximo: os mais antigos saem (com animação).
    for (const velho of ativos.slice(MAXIMO - 1)) push.fechar(velho.id);
    emitir();
    return id;
  },

  atualizar(id: string, d: Partial<DadosPush>) {
    let mudou = false;
    lista = lista.map((p) => {
      if (p.id !== id) return p;
      mudou = true;
      return { ...p, ...d, versao: p.versao + 1 };
    });
    if (mudou) emitir();
  },

  fechar(id: string) {
    const p = lista.find((x) => x.id === id);
    if (!p || p.saindo) return;
    lista = lista.map((x) => (x.id === id ? { ...x, saindo: true } : x));
    emitir();
    setTimeout(() => {
      lista = lista.filter((x) => x.id !== id);
      emitir();
    }, DURACAO_SAIDA_MS);
    try {
      p.aoFechar?.();
    } catch {
      /* ignora */
    }
  },

  existe(id: string): boolean {
    return lista.some((p) => p.id === id && !p.saindo);
  },

  observar(fn: () => void): () => void {
    ouvintes.add(fn);
    return () => {
      ouvintes.delete(fn);
    };
  },

  lista(): Push[] {
    return lista;
  },
};
